"use client"

import Link from "next/link"
import { Lock, Minus, Plus, ShoppingBag, Trash2, Truck } from "lucide-react"

import { cn } from "@/lib/utils"
import { useCart } from "@/lib/cart-store"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"

function formatEuros(n: number) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(n)
}

export function CartView() {
  const { items, hydrated, update, remove, clear, subtotal } = useCart()

  if (!hydrated) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          Chargement du panier…
        </CardContent>
      </Card>
    )
  }

  if (items.length === 0) {
    return (
      <Card
        className={cn(
          "mx-auto max-w-xl text-center",
          "rounded-[2rem] p-1.5",
          "bg-foreground/[0.02] ring-1 ring-foreground/5",
        )}
      >
        <div className="rounded-[calc(2rem-0.375rem)] bg-card p-8 md:p-10">
          <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ShoppingBag className="size-6" />
          </div>
          <h2 className="mt-4 text-xl font-bold tracking-tight">
            Votre panier est vide
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Parcourez notre catalogue outillage pour ajouter vos premiers
            articles.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Button asChild className="rounded-full">
              <Link href="/outillage">Parcourir le catalogue</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full">
              <Link href="/affutage">Demander un affûtage</Link>
            </Button>
          </div>
        </div>
      </Card>
    )
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      <Card>
        <CardHeader>
          <CardTitle>Articles</CardTitle>
          <CardDescription>
            {items.length} produit{items.length > 1 ? "s" : ""} dans votre
            panier
          </CardDescription>
        </CardHeader>
        <CardContent className="divide-y">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex flex-col gap-4 py-5 first:pt-0 last:pb-0 sm:flex-row sm:items-center"
            >
              {item.image ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={item.image}
                  alt={item.name}
                  className="size-20 shrink-0 rounded-lg bg-muted object-cover"
                />
              ) : (
                <div className="size-20 shrink-0 rounded-lg bg-muted" />
              )}
              <div className="flex-1 min-w-0">
                <Link
                  href={`/produit/${item.slug}`}
                  className="font-medium hover:underline underline-offset-4"
                >
                  {item.name}
                </Link>
                {item.brand ? (
                  <p className="text-xs text-muted-foreground">{item.brand}</p>
                ) : null}
                <p className="mt-1 text-sm font-semibold tabular-nums">
                  {formatEuros(item.unitPrice)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="size-9 rounded-full"
                  onClick={() =>
                    update(item.id, Math.max(item.quantity - 1, 0))
                  }
                  aria-label="Réduire la quantité"
                >
                  <Minus className="size-4" />
                </Button>
                <span className="w-8 text-center text-sm tabular-nums">
                  {item.quantity}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="size-9 rounded-full"
                  onClick={() => update(item.id, item.quantity + 1)}
                  aria-label="Augmenter la quantité"
                >
                  <Plus className="size-4" />
                </Button>
              </div>
              <div className="text-end">
                <p className="text-sm font-medium tabular-nums">
                  {formatEuros(item.unitPrice * item.quantity)}
                </p>
                <button
                  type="button"
                  onClick={() => remove(item.id)}
                  className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive transition-colors"
                >
                  <Trash2 className="size-3" />
                  Retirer
                </button>
              </div>
            </div>
          ))}
        </CardContent>
        <CardFooter>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clear}
            className="text-muted-foreground"
          >
            Vider le panier
          </Button>
        </CardFooter>
      </Card>

      <Card className="h-fit">
        <CardHeader>
          <CardTitle>Récapitulatif</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Sous-total HT</span>
            <span className="font-medium tabular-nums">
              {formatEuros(subtotal)}
            </span>
          </div>
          <div className="flex items-center justify-between text-muted-foreground">
            <span>Livraison</span>
            <span>Calculée à l&apos;étape suivante</span>
          </div>
          <Separator />
          <div className="flex items-center justify-between text-base">
            <span className="font-semibold">Total estimé HT</span>
            <span className="font-bold tabular-nums">
              {formatEuros(subtotal)}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            TVA 20 % ajoutée à la facture finale après confirmation de
            l&apos;adresse et du statut TVA.
          </p>

          {/*
           * Trust signals row — reassures the buyer right next to the
           * primary action. The CRO audit identified the cart page as
           * the second-highest abandonment surface (after checkout
           * itself) and trust badges adjacent to the CTA materially
           * lift conversion in B2B.
           */}
          <div className="mt-2 grid gap-2 rounded-xl border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
            <div className="flex items-start gap-2">
              <Truck className="mt-0.5 size-3.5 shrink-0 text-primary" />
              <span>Livraison sous 24–48 h en Île-de-France.</span>
            </div>
            <div className="flex items-start gap-2">
              <Lock className="mt-0.5 size-3.5 shrink-0 text-primary" />
              <span>
                Paiement sécurisé Stripe — aucune information bancaire
                stockée sur ADIS 95.
              </span>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-3">
          <Button
            asChild
            size="lg"
            className="w-full rounded-full"
            disabled={items.length === 0}
          >
            <Link href="/panier/commander">Passer au paiement</Link>
          </Button>
          <Button
            asChild
            variant="outline"
            size="lg"
            className="w-full rounded-full"
          >
            <Link href="/outillage">Continuer mes achats</Link>
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}
